import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { BellIcon, CheckIcon, DownloadIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { DownloadLink } from "simplydo/react/components/DownloadLink";
import {
  useWebsocketEvent,
  useWebsocketReconnect,
} from "simplydo/react/hooks/useWebsocket";
import { RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { MenuItem } from "simplydo/react/ui/menu";
import { Popup } from "simplydo/react/ui/popup";
import { Tab } from "simplydo/react/ui/tab";

import api, { websocketApi } from "api";
import { useCurrentUser } from "utils/currentUser";

import CircularButtonWithNumber from "../CircularButtonWithNumber";
import ModalCarousel, { type ModalCarouselScreen } from "../ModalCarousel";
import Downloads from "./Downloads";
import Notifications from "./Notifications";
import { refreshTaskResult } from "./refreshTaskResult";
import type { AnnouncementNotification, AppNotification } from "./types";

const StyledNotificationTray = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: min(600px, 80vh);
  flex-direction: column;
  z-index: 9999;
`;

type NotificationTrayProps = {
  isMobile?: boolean;
  style?: React.CSSProperties;
};

export const useTrayStatus = () => {
  type TrayStatus = { open: boolean; activeIndex: number };

  const defaultState: TrayStatus = { open: false, activeIndex: 0 };
  const openState = useQuery({
    queryKey: ["notificationTray"],
    queryFn: async () => defaultState,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  const setTrayStatus = useCallback((trayStatus: Partial<TrayStatus>) => {
    api.queryClient.setQueryData(
      ["notificationTray"],
      (prevStatus: TrayStatus) => ({ ...prevStatus, ...trayStatus }),
    );
  }, []);

  return [openState.data || defaultState, setTrayStatus] as const;
};

const NotificationTray = ({ isMobile, style }: NotificationTrayProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [trayStatus, setTrayStatus] = useTrayStatus();
  const [page, setPage] = useState(1);
  const [downloadCompleted, setDownloadCompleted] = useState(false);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [pendingTasks, setPendingTasks] = useState<string[]>([]);
  const [announcementNotification, setAnnouncementNotification] =
    useState<AnnouncementNotification | null>(null);

  const userId = user?._id;
  const invitationsQuery = useQuery(
    initUseQuery(api.invitations.get, [], { isEnabled: !!userId }),
  );
  // Non-acceptable invitations aren't really interesting to the user in terms of notification content. They may be able to remove them, but it's not really "notification" worthy that e.g., another admin has invited a user to one of their groups
  const acceptableInvitations = useMemo(
    () =>
      invitationsQuery.data?.invitations.filter(
        (invitation) =>
          !invitation.createdByCurrentUser && invitation.canAccept,
      ) || [],
    [invitationsQuery.data?.invitations],
  );

  useWebsocketEvent(
    websocketApi,
    "pushNotification",
    () => {
      api.invalidateQueries(api.notifications.get);
    },
    !!userId,
  );

  useWebsocketEvent(
    websocketApi,
    "downloadCompleted",
    () => {
      setDownloadCompleted(true);
      setTrayStatus({ open: true, activeIndex: 1 });
      api.invalidateQueries(api.users.getDownloads);
    },
    !!userId,
  );

  useWebsocketReconnect(websocketApi, () => {
    api.invalidateQueries(api.notifications.get);
    api.invalidateQueries(api.users.getDownloads);
  });

  const notificationsQuery = useQuery(
    initUseQuery(api.notifications.get, [page], {
      isEnabled: !!userId,
      refetchInterval: ({ state }) => {
        if (
          (state.data?.notifications?.filter(
            (notif) => "status" in notif && notif.status === "pending",
          )?.length ?? 0) > 0
        ) {
          return 5000;
        }
        return false;
      },
    }),
  );

  useEffect(() => {
    // We monitor pending tasks to see if any have completed since the last check and if so, we open the tray
    const newPendingTasks =
      notificationsQuery.data?.notifications
        ?.filter((n) => "status" in n && n.status === "pending")
        ?.map((n) => n._id) ?? [];
    if (newPendingTasks.length === 0 && pendingTasks.length === 0) {
      return;
    }
    const completedTasks = pendingTasks.filter(
      (taskId) => !newPendingTasks.includes(taskId),
    );
    if (completedTasks.length > 0) {
      setTrayStatus({ open: true, activeIndex: 0 });
      notificationsQuery.data?.notifications
        ?.filter((n) => completedTasks.includes(n._id))
        .forEach(refreshTaskResult);
    }
    if (
      newPendingTasks.length === pendingTasks.length &&
      newPendingTasks.every((id, index) => id === pendingTasks[index])
    ) {
      return;
    }
    setPendingTasks(newPendingTasks);
  }, [notificationsQuery.data?.notifications, pendingTasks, setTrayStatus]);

  const markAllAsSeen = useCallback(() => {
    api.notifications.markAllAsSeen(
      () => {
        api.invalidateQueries(api.notifications.get);
      },
      () => {},
    );
  }, []);
  const notificationContent = (
    <StyledNotificationTray>
      <Tab
        defaultActiveIndex={0}
        activeIndex={trayStatus.activeIndex}
        onTabChange={(_e, { activeIndex }) => {
          setPage(1);
          setTrayStatus({ activeIndex: activeIndex as number });
        }}
        variant="underline"
        style={{ position: "relative", margin: 0 }}
        panes={[
          {
            menuItem: (
              <MenuItem>
                <Icon icon={BellIcon} />
                Notifications
              </MenuItem>
            ),
            render: () => (
              <Notifications
                notifications={
                  (notificationsQuery.data?.notifications ||
                    []) as AppNotification[]
                }
                total={notificationsQuery.data?.total || 0}
                unread={notificationsQuery.data?.unread || 0}
                acceptableInvitations={acceptableInvitations}
                invitations={invitationsQuery.data?.invitations || []}
                page={page}
                setPage={setPage}
                setAnnouncementNotification={setAnnouncementNotification}
                setAnnouncementModalOpen={setAnnouncementModalOpen}
                loading={notificationsQuery.isLoading}
              />
            ),
          },
          {
            menuItem: (
              <MenuItem>
                <Icon icon={DownloadIcon} />
                Downloads
              </MenuItem>
            ),
            render: () => (
              <Downloads
                downloadCompleted={downloadCompleted}
                page={page}
                setPage={setPage}
              />
            ),
          },
        ]}
      />
    </StyledNotificationTray>
  );

  if (isMobile) {
    return notificationContent;
  }

  const notificationTrigger = (
    <CircularButtonWithNumber
      style={style}
      onClick={() => {
        if (theme.sizes.isMobile) {
          markAllAsSeen();
          if (location.pathname === "/notifications") {
            navigate("..");
          } else {
            navigate("/notifications");
          }
        }
      }}
      icon={BellIcon}
      aria-label={t("notifications.title")}
      count={notificationsQuery.data?.unread ?? 0}
    />
  );

  const announcementModal = announcementNotification?.events ? (
    <ModalCarousel
      open={announcementModalOpen}
      onClose={() => setAnnouncementModalOpen(false)}
      screens={announcementNotification.events.map(
        (event): ModalCarouselScreen => {
          const announcement = event.ownerAnnouncement;
          const bodyValue =
            typeof announcement?.body === "string" ? announcement.body : "";
          const attachments = announcement?.attachments ?? [];

          return {
            title: announcement?.subject ?? "",
            content: (
              <div>
                {announcement?.body ? (
                  <RichTextViewer value={bodyValue} />
                ) : (
                  <p>{announcement?.text}</p>
                )}
                {attachments.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <H5>Attachments</H5>
                    {attachments.map((attachment) => (
                      <DownloadLink
                        key={attachment.url}
                        url={attachment.url ?? ""}
                        name={attachment.name ?? ""}
                        className="ui small blue button"
                        style={{ borderRadius: 5 }}
                      >
                        <i className="paperclip icon"></i>
                        {attachment.name}
                      </DownloadLink>
                    ))}
                  </div>
                )}
                <p style={{ color: "grey" }}>
                  {announcement?.createdAt
                    ? ` ${formatDate(announcement.createdAt)}`
                    : ""}
                </p>
              </div>
            ),
          };
        },
      )}
      actions={[
        {
          label: "Close",
          icon: CheckIcon,
          primary: true,
          onClick: () => {
            setAnnouncementModalOpen(false);
          },
        },
      ]}
    />
  ) : null;

  // Mobile uses a dedicated /notifications page — never open the desktop
  // popup tray (its fixed preferred width overflows small viewports).
  if (theme.sizes.isMobile) {
    return (
      <>
        {announcementModal}
        {notificationTrigger}
      </>
    );
  }

  return (
    <>
      {announcementModal}

      <Popup
        on="click"
        fluid
        style={{ width: "min(550px, calc(100vw - 16px))" }}
        wide="very"
        position="bottom right"
        offset={[5, 9]}
        open={trayStatus.open}
        onOpen={() => {
          // Side effects on open only — do not also toggle open in the
          // trigger onClick. Popup useClick owns open/close; a second
          // setTrayStatus toggle races it and can leave the tray closed.
          markAllAsSeen();
          api.invalidateQueries(api.users.getDownloads);
          setDownloadCompleted(false);
          setTrayStatus({ open: true });
        }}
        onClose={() => {
          setDownloadCompleted(false);
          setTrayStatus({ open: false });
        }}
        trigger={notificationTrigger}
      >
        {notificationContent}
      </Popup>
    </>
  );
};

export default NotificationTray;
